import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL, getToken } from "../../shared/utils.js/utils";
import { makeOfflineRequest } from "../../shared/utils.js/offlineRequestManager";
import i18n from "../../i18n/i18n";

// Get Equipments
export const getEquipments = createAsyncThunk(
  "equipments/getEquipments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append("is_active", params.is_active);
      }

      const url = `${BASE_URL}/equipment${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get equipments!";
      return rejectWithValue(message);
    }
  },
);

// Get equipment Details
export const getEquipmentDetails = createAsyncThunk(
  "equipments/getEquipmentDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/equipment/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get equipment details!";
      return rejectWithValue(message);
    }
  },
);

// Delete Equipment
export const deleteEquipments = createAsyncThunk(
  "equipment/deleteEquipments",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      // Use offline-aware request
      let responseData;
      if (navigator.onLine) {
        const response = await axios.delete(`${BASE_URL}/equipment/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language,
          },
        });
        responseData = response.data;
      } else {
        // Queue DELETE request for offline
        await makeOfflineRequest(`/equipment/${id}`, "DELETE", undefined, {
          "Accept-Language": i18n.language,
        });
        responseData = { success: true, offline: true };
      }
      return responseData;
    } catch (error) {
      // Treat offline save as success
      if (error.message === "OFFLINE_REQUEST_SAVED" || error.isOffline) {
        return { success: true, offline: true };
      }
      const message =
        error.response?.data?.message || "Failed to delete equipment!";
      return rejectWithValue(message);
    }
  },
);

// Toggle Equipment Status
export const toggleEquipmentStatus = createAsyncThunk(
  "equipment/toggleEquipmentStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No authentication token found");
      let responseData;
      if (navigator.onLine) {
        const response = await axios.patch(
          `${BASE_URL}/equipment/${id}/toggle-status`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Accept-Language": i18n.language,
              "Content-Type": "application/json",
            },
          },
        );
        responseData = response.data;
      } else {
        // Queue PATCH request for offline
        await makeOfflineRequest(
          `/equipment/${id}/toggle-status`,
          "PATCH",
          {},
          {
            "Accept-Language": i18n.language,
            "Content-Type": "application/json",
          },
        );
        responseData = { success: true, offline: true };
      }
      return responseData;
    } catch (error) {
      // Treat offline save as success
      if (error.message === "OFFLINE_REQUEST_SAVED" || error.isOffline) {
        return { success: true, offline: true };
      }
      const message =
        error.response?.data?.message || "Failed to toggle Equipment status!";
      return rejectWithValue(message);
    }
  },
);

// Add Equipment
export const addEquipment = createAsyncThunk(
  "equipment/addEquipment",
  async (equipmentData, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/equipment`,
        equipmentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add Equipment!";
      return rejectWithValue(message);
    }
  },
);

// update Equipment
export const updateEquipment = createAsyncThunk(
  "equipment/updateEquipment",
  async ({ id, equipmentData }, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(
        `${BASE_URL}/equipment/${id}`,
        equipmentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": i18n.language,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update equipment!";
      return rejectWithValue(message);
    }
  },
);
